import React from 'react';

export const desktopRibbonDividerClass = 'mx-1 h-8 w-px shrink-0 bg-slate-300 dark:bg-slate-600';
export const desktopRibbonGroupClass = 'flex shrink-0 items-center gap-0.5';
export const desktopRibbonInputClass =
  'h-[30px] rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[11px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-secondary)] focus:border-[var(--brand-primary)] transition-colors';
export const desktopRibbonSelectClass =
  'h-[30px] min-w-[100px] rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[11px] text-[var(--text-primary)] outline-none hover:border-[var(--brand-primary)] transition-colors';

export const desktopRibbonButtonClass =
  'group relative overflow-hidden flex w-[58px] h-[42px] flex-col items-center justify-center gap-[3px] rounded-[3px] border-0 border-b-2 border-b-transparent bg-transparent px-1 text-center transition-colors hover:bg-[var(--ribbon-button-hover)] hover:text-[var(--ribbon-text-primary)] active:bg-[var(--ribbon-button-active)] dark:hover:bg-[var(--ribbon-button-hover)] dark:hover:text-[var(--ribbon-text-primary)] dark:active:bg-[var(--ribbon-button-active)] disabled:cursor-not-allowed disabled:opacity-40';

export const desktopRibbonButtonActiveClass =
  'group relative overflow-hidden flex w-[58px] h-[42px] flex-col items-center justify-center gap-[3px] rounded-[3px] border-0 border-b-2 border-b-[var(--nav-active-bar)] bg-[var(--ribbon-button-active)] px-1 text-center transition-colors hover:bg-[var(--ribbon-button-hover)] dark:bg-[var(--ribbon-button-active)] dark:hover:bg-[var(--ribbon-button-hover)] disabled:cursor-not-allowed disabled:opacity-40';

export const desktopRibbonIconOnlyButtonClass =
  'group relative overflow-hidden flex items-center justify-center w-[28px] h-[28px] rounded-sm border-0 border-b-2 border-b-transparent bg-transparent transition-colors hover:bg-blue-50 hover:text-blue-700 active:bg-blue-100 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 dark:active:bg-blue-900/30 disabled:cursor-not-allowed disabled:opacity-40';

export const desktopRibbonIconOnlyButtonActiveClass =
  'group relative overflow-hidden flex items-center justify-center w-[28px] h-[28px] rounded-sm border-0 border-b-2 border-b-[var(--nav-active-bar)] bg-blue-50 transition-colors hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 disabled:cursor-not-allowed disabled:opacity-40';

/** Dropdown-open state: hover background retained, no bottom bar */
export const desktopRibbonButtonDropdownOpenClass =
  'group relative overflow-hidden flex w-[58px] h-[42px] flex-col items-center justify-center gap-[3px] rounded-[3px] border-0 border-b-2 border-b-transparent bg-[var(--ribbon-button-active)] px-1 text-center transition-colors dark:bg-[var(--ribbon-button-active)] disabled:cursor-not-allowed disabled:opacity-40';

interface RibbonLabeledGroupProps {
  label: string;
  children: React.ReactNode;
}
/** Ribbon group with a bottom section label — fits inside the 46px ribbon row */
export function RibbonLabeledGroup({ label, children }: RibbonLabeledGroupProps) {
  return (
    <div className="flex h-full shrink-0 flex-col">
      <div className="flex min-h-0 flex-1 items-center gap-1 px-0">
        {children}
      </div>
      <div className="flex h-[10px] shrink-0 items-center justify-center px-1">
        <span className="max-w-[180px] truncate text-center text-[8px] font-semibold uppercase leading-none tracking-[0.06em] text-[var(--ribbon-text-secondary)]">
          {label}
        </span>
      </div>
    </div>
  );
}
export const desktopRibbonIconClass =
  'text-[var(--ribbon-text-secondary)] transition-colors group-hover:text-[var(--ribbon-text-primary)] [&_svg]:h-[18px] [&_svg]:w-[18px]';
export const activeDesktopRibbonIconClass = 'text-blue-600 dark:text-blue-400 transition-colors [&_svg]:h-[18px] [&_svg]:w-[18px]';
export const desktopRibbonLabelClass =
  'text-[10px] leading-none font-medium whitespace-nowrap text-center text-[var(--ribbon-text-secondary)] transition-colors group-hover:text-[var(--ribbon-text-primary)]';
export const activeDesktopRibbonLabelClass =
  'text-[10px] leading-none font-semibold whitespace-nowrap text-center text-blue-600 dark:text-blue-400';

interface RibbonDesktopButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
  title?: string;
  className?: string;
  hasDropdown?: boolean;
}

export function RibbonDesktopButton({
  icon,
  label,
  onClick,
  active = false,
  disabled = false,
  title,
  className = '',
  hasDropdown = false,
}: RibbonDesktopButtonProps) {
  const iconOnlyCls = label ? '' : 'w-[32px]';
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={title ?? label}
      className={`${active ? desktopRibbonButtonActiveClass : desktopRibbonButtonClass} ${iconOnlyCls} ${className}`.trim()}
    >
      <span className={active ? activeDesktopRibbonIconClass : desktopRibbonIconClass}>{icon}</span>
      {label && (
        hasDropdown ? (
          <span className="flex items-center gap-[2px]">
            <span className={active ? activeDesktopRibbonLabelClass : desktopRibbonLabelClass}>{label}</span>
            <svg width="7" height="5" viewBox="0 0 7 5" fill="none" xmlns="http://www.w3.org/2000/svg"
              className="pointer-events-none shrink-0 text-[var(--ribbon-text-secondary)] opacity-60"
              aria-hidden="true">
              <path d="M1 1L3.5 4L6 1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        ) : (
          <span className={active ? activeDesktopRibbonLabelClass : desktopRibbonLabelClass}>{label}</span>
        )
      )}
      {hasDropdown && !label && (
        <svg width="7" height="5" viewBox="0 0 7 5" fill="none" xmlns="http://www.w3.org/2000/svg"
          className="pointer-events-none shrink-0 text-[var(--ribbon-text-secondary)] opacity-60"
          aria-hidden="true">
          <path d="M1 1L3.5 4L6 1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
}
